import React, { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import { Add, EditOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import financeService from '../../../../services/financeService';
import { Reconciliation } from '../../../../types/finance';
import { useAuth } from '../../../../context/AuthContext';
import { formatPaisa } from '../../../../utils/paisa';
import ConfirmationDialog from '../../../common/ConfirmationDialog';
import ReconciliationSummary from './ReconciliationSummary';
import ReconciliationDownload from './ReconciliationDownload';
import ReconciliationLinesTable from './ReconciliationLinesTable';
import StatementDialog from './StatementDialog';
import BankAdjustmentDialog from './BankAdjustmentDialog';

/**
 * An open reconciliation: tick the lines the statement shows until the
 * cleared balance meets it, add what only the bank knew about, then complete.
 */
const ReconciliationWorkspace: React.FC<{
  rec: Reconciliation;
  editable: boolean;
  /** Apply a change locally before the server confirms it. */
  onLocal: (rec: Reconciliation) => void;
  onReload: () => Promise<void>;
  onOpenEntry: (entryId: string) => void;
  onDone: () => void;
}> = ({ rec, editable, onLocal, onReload, onOpenEntry, onDone }) => {
  const theme = useTheme();
  const { currency } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState<'complete' | 'discard' | null>(null);
  const [busy, setBusy] = useState(false);
  const balanced = rec.summary.differencePaisa === 0;
  const statement = useMemo(
    () => ({ statementDate: rec.statementDate, statementBalancePaisa: rec.statementBalancePaisa }),
    [rec.statementDate, rec.statementBalancePaisa],
  );

  const toggle = async (lineIds: string[], cleared: boolean) => {
    if (!lineIds.length) return;
    const ids = new Set(lineIds);
    onLocal({ ...rec, lines: rec.lines.map(l => (ids.has(l._id) ? { ...l, cleared } : l)) });
    try {
      await financeService.setCleared(rec._id, lineIds, cleared);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'The change could not be saved.', { variant: 'error' });
    }
    await onReload();
  };

  const finish = async () => {
    setBusy(true);
    try {
      if (confirm === 'complete') {
        await financeService.completeReconciliation(rec._id);
        enqueueSnackbar(`${rec.accountName} reconciled`, { variant: 'success' });
        await onReload();
      } else {
        await financeService.discardReconciliation(rec._id);
        enqueueSnackbar('Reconciliation discarded', { variant: 'success' });
        onDone();
      }
      setConfirm(null);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'That did not go through.', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontWeight: 500, mr: 1 }}>{rec.accountCode}</Box>
            {rec.accountName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tick each line that appears on the statement. Lines left unticked carry to the next reconciliation.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {editable && (
            <>
              <Button size="small" startIcon={<EditOutlined />} onClick={() => setEditing(true)}>Statement</Button>
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setAdding(true)}>Charge or profit</Button>
            </>
          )}
          <ReconciliationDownload rec={rec} />
        </Stack>
      </Stack>

      <ReconciliationSummary rec={rec} />

      <Box sx={{ mt: 2 }}>
        <ReconciliationLinesTable lines={rec.lines} editable={editable} onToggle={toggle} onOpenEntry={onOpenEntry} />
      </Box>

      {editable && (
          <Paper
            elevation={6}
            sx={{
              position: 'sticky', bottom: 16, mt: 2, mx: 'auto', maxWidth: 720, borderRadius: 3, px: 2, py: 1.25,
              border: `1px solid ${alpha(balanced ? theme.palette.success.main : theme.palette.warning.main, 0.4)}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="body2" sx={{ flex: 1 }} color={balanced ? 'success.dark' : 'text.secondary'} fontWeight={balanced ? 600 : 400}>
                {balanced
                  ? 'The cleared balance matches the statement.'
                  : <>The statement is {rec.summary.differencePaisa > 0 ? 'higher' : 'lower'} by <b>{currency} {formatPaisa(Math.abs(rec.summary.differencePaisa))}</b> — keep ticking, or add a charge or profit.</>}
              </Typography>
              <Button color="inherit" onClick={() => setConfirm('discard')}>Discard</Button>
              <Button variant="contained" disableElevation color="success" disabled={!balanced} onClick={() => setConfirm('complete')}>Complete</Button>
            </Stack>
          </Paper>
      )}

      <StatementDialog
        open={editing}
        title="Statement"
        subtitle="Correct the statement date or closing balance."
        initial={statement}
        submitLabel="Save"
        onClose={() => setEditing(false)}
        onSubmit={async statement => { await financeService.updateReconciliation(rec._id, statement); await onReload(); }}
      />
      <BankAdjustmentDialog open={adding} reconciliationId={rec._id} statementDate={rec.statementDate} onClose={() => setAdding(false)} onPosted={onReload} />
      <ConfirmationDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={finish}
        loading={busy}
        title={confirm === 'complete' ? 'Complete the reconciliation?' : 'Discard this reconciliation?'}
        message={confirm === 'complete'
          ? `${rec.accountName} will be reconciled to the statement. The ticked lines are settled; the rest carry forward.`
          : 'The ticks are lost. Any charge or profit you added stays in the books and can be ticked next time.'}
        confirmText={confirm === 'complete' ? 'Complete' : 'Discard'}
        confirmColor={confirm === 'complete' ? 'success' : 'error'}
      />
    </Box>
  );
};

export default ReconciliationWorkspace;
